import type { OperationVariables } from '@apollo/client'
import { useQuery as useBaseQuery } from '@apollo/client/react/compiled'
import type { PrefixedPermissions } from '@dolpheen/apollo'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import useAppLoader from '@hooks/useAppLoader'
import useUser from '@hooks/useUser'

import { getPermissionVariables } from './permissionVariables'

export type UseQueryResult<TData, TVariables extends OperationVariables> = useBaseQuery.Result<
  TData,
  TVariables,
  'empty' | 'complete'
>

export type QueryHookOptions<TData, TVariables extends OperationVariables> = Partial<
  Omit<useBaseQuery.Options<TData, TVariables>, 'variables'> & {
    displayLoader: boolean
    variables?: TVariables
  }
>
export type UseQueryHook<TData, TVariables extends OperationVariables> = (
  opts?: QueryHookOptions<TData, TVariables>,
) => UseQueryResult<TData, TVariables>

export function useQuery<TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  { displayLoader, variables: _bareVariables, ...opts }: QueryHookOptions<TData, TVariables> = {},
): UseQueryResult<TData, TVariables> {
  const user = useUser()

  const variables = {
    ..._bareVariables,
    ...getPermissionVariables(user.user?.accountPermissions),
  } as TVariables & Record<PrefixedPermissions, boolean>

  // AC 4.2 modern signatures are inference-only (no explicit generics) and
  // their conditional options tuple doesn't resolve for an unbound generic
  // TVariables, so widen variables to OperationVariables at the call and
  // narrow the result back to the wrapper's stable public type.
  const queryData = useBaseQuery(
    query as TypedDocumentNode<TData, OperationVariables>,
    {
      ...opts,
      variables,
    } as useBaseQuery.Options<TData, OperationVariables>,
  ) as UseQueryResult<TData, TVariables>

  useAppLoader(displayLoader ? queryData.loading : false)

  return queryData
}

function makeQuery<TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
): UseQueryHook<TData, TVariables> {
  return (opts: QueryHookOptions<TData, TVariables>) => useQuery<TData, TVariables>(query, opts)
}

export default makeQuery
